import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
// graphql
import { SYSTEM_PROCESSES_LIST_QUERY } from 'graphql/systemProcesses/queries'
import { RELATED_RISKS_PERMISSIONS_QUERY, RISKS_QUERY } from 'graphql/risk/queries'
// types
import {
  FieldType,
  IRiskTemplate,
  ISystemProcess,
  IRisksResponse,
  IPermissionsMap,
  SystemProcessObject,
  ISystemProcessCondition,
  ISystemProcessTransaction,
  SystemProcessConditionAction,
  SystemProcessTransactionType
} from 'types/graphql'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// hooks
import { useRouter } from 'hooks/useRouter'
import { useParentRiskId } from 'hooks/risks/useParentRiskId'
// store
import { addRelatedForm } from 'store/reducers/relatedForms'
// utils
import { get } from 'lodash'

type QueryResponse = {
  risks: IRisksResponse
}

export const useParentRelatedForms = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = React.useState(false)

  const { risk } = React.useContext(RiskAPIContext)

  const parentRiskId = useParentRiskId(risk)

  const relatedForms = useSelector((state: any) => state.relatedForms[parentRiskId] || [])

  const skipQueries = !parentRiskId || !!relatedForms.length

  // The parent's template: known from the record everywhere except the multiple route, which has no
  // record and carries it in the URL instead.
  const parentTemplateId = (risk?.parentRisk?.riskTemplate?.id || router.query.parentTemplateId) as string

  // The parent's own record is fetched for its saved values, which the processes are evaluated
  // against. `riskTemplateId` is not optional in practice: filtering by `ids` alone comes back empty.
  const skipParentQuery =
    !parentRiskId || !parentTemplateId || (!!relatedForms.length && !!risk?.parentRisk)

  const { data } = useQuery<QueryResponse>(RISKS_QUERY, {
    skip: skipParentQuery,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ids: [parentRiskId],
        riskTemplateId: parentTemplateId
      },
      pagination: {
        pageSkip: 0,
        pageTake: 1
      }
    }
  })

  const parentRisk = risk?.parentRisk || data?.risks?.data?.[0]

  const { data: systemProcessesQuery } = useQuery(SYSTEM_PROCESSES_LIST_QUERY, {
    skip: skipQueries || !parentTemplateId,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        objectId: parentTemplateId,
        objects: [SystemProcessObject.RiskTemplate]
      },
      pagination: {
        pageTake: 1000,
        pageSkip: 0
      }
    }
  })

  const { data: relatedRisksPermissionsQuery, loading: permissionsLoading } = useQuery(
    RELATED_RISKS_PERMISSIONS_QUERY,
    {
      skip: skipQueries || !parentTemplateId,
      fetchPolicy: 'network-only',
      variables: {
        riskId: parentRiskId,
        riskTemplateId: parentTemplateId
      }
    }
  )

  const fieldValues = React.useMemo(() => data?.risks?.data?.[0]?.fieldValueMap, [data])

  const riskTemplates: IRiskTemplate[] = get(
    relatedRisksPermissionsQuery,
    'getRelatedFormsPermissions.riskTemplates',
    []
  )

  const relatedFormsPermissions: IPermissionsMap[] = get(
    relatedRisksPermissionsQuery,
    'getRelatedFormsPermissions.permissionsMap',
    []
  )

  // Keyed on the response, not on `loading`: this query only starts once the record has arrived and
  // supplied the parent's template, so `loading` can read false both before the request and after
  // it, leaving a memo keyed on it stuck at the empty array it was first computed with.
  const systemProcesses: ISystemProcess[] = React.useMemo(
    () => get(systemProcessesQuery, 'systemProcesses.data', []).filter(({ active }) => active),
    [systemProcessesQuery]
  )

  const spWithCondition: ISystemProcess[] = React.useMemo(
    () => systemProcesses.filter(({ conditions }) => !!conditions.length),
    [systemProcesses.length]
  )

  const spWithoutCondition: ISystemProcess[] = React.useMemo(
    () => systemProcesses.filter(({ conditions }) => !conditions.length),
    [systemProcesses.length]
  )

  const checkCondition = (condition: ISystemProcessCondition) => {
    const fieldValue = fieldValues.find((item) => item.fieldId === condition.fieldName)?.fieldValue

    if (condition.action === SystemProcessConditionAction.Is) {
      if (condition.fieldType === FieldType.PickList) {
        if (Array.isArray(fieldValue)) {
          return fieldValue?.some(({ value }) => value === condition.value?.value)
        }
        return fieldValue?.value === condition.value?.value
      }
      return fieldValue === condition.value?.value
    }

    if (condition.action === SystemProcessConditionAction.IsNot) {
      if (condition.fieldType === FieldType.PickList) {
        if (Array.isArray(fieldValue)) {
          return fieldValue?.every(({ value }) => value !== condition.value?.value)
        }
        return fieldValue?.value !== condition.value?.value
      }
      return fieldValue !== condition.value?.value
    }
  }

  const processTransactions = async ({
    processes,
    skipConditions
  }: {
    processes: ISystemProcess[]
    skipConditions?: boolean
  }) => {
    for (const process of processes) {
      const { conditions, transactions } = process

      const shouldExecute = conditions.some((orCondition) =>
        orCondition.conditions.every((andCondition) => checkCondition(andCondition))
      )

      if (shouldExecute || skipConditions) {
        for (const transaction of transactions) {
          await executeTransaction(transaction as any)
        }
      }
    }
  }

  const executeTransaction = async (transaction: ISystemProcessTransaction & { id: string }) => {
    if (
      transaction.transactionType === SystemProcessTransactionType.ShowRelatedForm &&
      !relatedForms.some(({ riskTemplateId }) => riskTemplateId === transaction.relatedForms.riskTemplateId)
    ) {
      const relatedFormPermissions = relatedFormsPermissions.find(
        ({ riskTemplateId }) => riskTemplateId === transaction.relatedForms.riskTemplateId
      )

      const relatedTemplate = riskTemplates.find(({ id }) => id === transaction.relatedForms.riskTemplateId)

      if (relatedFormPermissions) {
        const relatedForm = {
          ...transaction.relatedForms,
          ...relatedFormPermissions,
          riskTemplate: relatedTemplate
        }

        dispatch(
          addRelatedForm({
            id: parentRiskId,
            form: relatedForm
          })
        )
      }
    }
  }

  const executeProcesses = async () => {
    setIsLoading(true)

    if (spWithCondition.length) {
      await processTransactions({ processes: spWithCondition })
    }

    if (spWithoutCondition.length) {
      await processTransactions({ processes: spWithoutCondition, skipConditions: true })
    }

    setIsLoading(false)
  }

  React.useEffect(() => {
    if (fieldValues && !permissionsLoading && systemProcesses.length && parentRiskId) {
      executeProcesses()
    }
  }, [permissionsLoading, systemProcesses.length, parentRiskId, JSON.stringify(fieldValues)])

  return { isLoading, parentRisk }
}
